// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Divider,
  Icon,
  Button,
  SimpleGrid,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MdLocationOn, MdAdd, MdEdit, MdDelete } from "react-icons/md";
import { formatDate, formatCurrency } from "utils/format";
import centersService from "services/centersService";

const getActivePhases = (center) => {
  if (Array.isArray(center?.phases)) {
    return center.phases.filter((phase) => phase?.status === "active");
  }

  if (center?.phase_config && typeof center.phase_config === "object") {
    return Object.entries(center.phase_config)
      .filter(([, config]) => config?.status === "active")
      .map(([name, config]) => {
        const amountMatch = (center.amounts || []).find((amount) => amount.phase === name);
        return {
          name,
          description: "",
          amount: amountMatch?.value,
          due_date: config?.due_date,
          status: config?.status,
        };
      });
  }

  return [];
};

export default function CentersManagement() {
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const emptyIconBg = useColorModeValue("gray.100", "whiteAlpha.100");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [centers, setCenters] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteCenter, setDeleteCenter] = useState(null);

  const fetchCenters = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await centersService.getCenters();
      if (result.success && result.data) {
        setCenters(result.data);
      } else {
        setError(result.error || "Failed to load centers");
      }
    } catch (err) {
      console.error("Error fetching centers:", err);
      setError("Failed to load centers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenters();
  }, []);

  const handleRequestDelete = (center) => {
    setDeleteCenter(center);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    if (!deleteCenter) return;
    setDeleting(true);
    try {
      const result = await centersService.deleteCenter(deleteCenter.id);
      if (result.success) {
        toast({
          title: "Success",
          description: result.message || "Center deleted",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteCenter(null);
        fetchCenters();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete center",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error deleting center:", err);
      toast({
        title: "Error",
        description: "Failed to delete center. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1000px" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" justify="space-between" flexWrap="wrap" gap="4">
          <Flex align="center" gap="3">
            <Flex
              w="12"
              h="12"
              borderRadius="xl"
              bg={iconBoxBg}
              align="center"
              justify="center"
            >
              <Icon as={MdLocationOn} color="brand.500" boxSize="6" />
            </Flex>
            <Box>
              <Text color={textColor} fontSize="2xl" fontWeight="700">
                Centers
              </Text>
              <Text color={textColorSecondary} fontSize="sm">
                {centers.length} center{centers.length !== 1 ? "s" : ""}
              </Text>
            </Box>
          </Flex>
          <Button
            leftIcon={<Icon as={MdAdd} />}
            colorScheme="brand"
            size="md"
            borderRadius="lg"
            onClick={() => navigate("/admin/centers/new")}
          >
            Add Center
          </Button>
        </Flex>

        {centers && centers.length > 0 ? (
          <SimpleGrid columns={{ base: 1 }} spacing="5">
            {centers.map((center) => {
              const activePhases = getActivePhases(center);
              return (
              <Card
                key={center.id}
                p="5"
                bg={cardBg}
                borderRadius="16px"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={borderColor}
                _hover={{ shadow: "md", borderColor: "brand.200" }}
                transition="all 0.2s"
              >
                <Flex direction="column" gap="4">
                  <Flex justify="space-between" align="flex-start">
                    <Flex
                      w="12"
                      h="12"
                      borderRadius="xl"
                      bg={iconBoxBg}
                      align="center"
                      justify="center"
                    >
                      <Icon as={MdLocationOn} color="brand.500" boxSize="6" />
                    </Flex>
                    <Flex gap="2">
                      <Button
                        size="xs"
                        leftIcon={<Icon as={MdEdit} />}
                        variant="outline"
                        colorScheme="brand"
                        onClick={() => navigate(`/admin/centers/${center.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="xs"
                        leftIcon={<Icon as={MdDelete} />}
                        variant="outline"
                        colorScheme="red"
                        onClick={() => handleRequestDelete(center)}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Flex>
                  <Box>
                    <Text color={textColor} fontSize="lg" fontWeight="700" mb="1">
                      {center.name}
                    </Text>
                    <Text color={textColorSecondary} fontSize="sm" noOfLines={2}>
                      {center.full_name}
                    </Text>
                  </Box>
                  <Text color={textColorSecondary} fontSize="sm">
                    Region: {center.region ? (center.region.name || center.region.code || center.region.id) : "—"}
                  </Text>
                  {activePhases.length > 0 && (
                    <Box>
                      <Text color={textColorSecondary} fontSize="xs" fontWeight="600" mb="1">
                        Active phases
                      </Text>
                      {activePhases.map((phase, idx) => (
                        <Text key={`${phase.name}-${idx}`} color={textColorSecondary} fontSize="xs" mb="1">
                          {phase.name}
                          {phase.description ? ` - ${phase.description}` : ""}
                          {Number.isFinite(Number(phase.amount)) ? ` - ${formatCurrency(Number(phase.amount))}` : ""}
                          {phase.due_date ? ` - due ${formatDate(phase.due_date)}` : ""}
                        </Text>
                      ))}
                    </Box>
                  )}
                  <Divider borderColor={borderColor} />
                  <Text color={textColorSecondary} fontSize="xs">
                    Added {center.created_at ? formatDate(center.created_at) : "—"}
                  </Text>
                </Flex>
              </Card>
              );
            })}
          </SimpleGrid>
        ) : (
          <Card p="0" bg={cardBg} overflow="hidden" borderRadius="16px" boxShadow="sm">
            <Flex direction="column" align="center" justify="center" py="16" px="6">
              <Flex
                w="16"
                h="16"
                borderRadius="full"
                bg={emptyIconBg}
                align="center"
                justify="center"
                mb="4"
              >
                <Icon as={MdLocationOn} color={textColorSecondary} boxSize="8" />
              </Flex>
              <Text color={textColor} fontWeight="600" mb="1">
                No centers yet
              </Text>
              <Text color={textColorSecondary} fontSize="sm" textAlign="center" mb="4">
                Add a center to get started.
              </Text>
              <Button
                leftIcon={<Icon as={MdAdd} />}
                colorScheme="brand"
                size="sm"
                onClick={() => navigate("/admin/centers/new")}
              >
                Add Center
              </Button>
            </Flex>
          </Card>
        )}
      </Flex>

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader color={textColor}>
              Delete center
            </AlertDialogHeader>
            <AlertDialogBody color={textColorSecondary}>
              Are you sure you want to delete “{deleteCenter?.name}”? This cannot be undone.
              If the center is assigned to students, you must reassign them first.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} isLoading={deleting} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
